// Multi-family: Households CRUD page (P1-10). Move-out is a soft state
// (sets moved_out_at via PATCH /households/:id) and is the closest thing
// to a delete for an active household.

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Button, Icon } from '@ng-unifi/ui';
import { CreateHouseholdBody, UpdateHouseholdBody } from '@ng-unifi/domain';
import { apiClient, ApiError } from '../lib/api.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';
import { Link } from 'react-router-dom';
import { CrudTable, fieldCls } from '../components/CrudTable.js';
import { PropertyScopeBar, scopedHref, usePropertyScope } from '../components/PropertyScope.js';
import { SearchInput } from '../components/SearchInput.js';
import { CrudModal, FormField } from '../components/CrudModal.js';
import { RowActions, Toast } from '../components/RowActions.js';
import { LocalOnlyBanner } from '../components/LocalOnlyBanner.js';

interface HouseholdRow {
  id: string;
  unit_id: string;
  label: string | null;
  moved_in_at: string;
  moved_out_at: string | null;
}

interface UnitOption {
  id: string;
  building_id: string;
  unit_number: string;
}

interface BuildingOption {
  id: string;
  name: string;
}

async function loadHouseholds(): Promise<HouseholdRow[]> {
  const res = await apiClient.get<{ data: HouseholdRow[] }>('/api/v1/households');
  return res.data;
}

async function loadUnits(): Promise<UnitOption[]> {
  const res = await apiClient.get<{ data: UnitOption[] }>('/api/v1/units');
  return res.data;
}

async function loadBuildings(): Promise<BuildingOption[]> {
  const res = await apiClient.get<{ data: BuildingOption[] }>('/api/v1/buildings');
  return res.data;
}

type ModalKind = 'create' | 'edit' | 'move-out' | 'delete';
type ModalState = { kind: ModalKind; row: HouseholdRow | null };

export function HouseholdsPage() {
  const [rows, setRows] = useState<HouseholdRow[] | null>(null);
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    Promise.all([loadHouseholds(), loadUnits(), loadBuildings()])
      .then(([hs, us, bs]) => {
        if (cancelled) return;
        setRows(hs);
        setUnits(us);
        setBuildings(bs);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  // Scoped from Units. Search and scope compose: the scope narrows to a unit,
  // the search box still filters within it.
  const { buildingId, unitId } = usePropertyScope();

  const unitLabel = (id: string) => {
    const u = units.find((x) => x.id === id);
    if (!u) return id;
    const b = buildings.find((x) => x.id === u.building_id);
    return b ? `${b.name} · ${u.unit_number}` : u.unit_number;
  };

  const [search, setSearch] = useState('');
  const filteredRows = useMemo(() => {
    if (!rows) return null;
    const q = search.trim().toLowerCase();
    if (q.length === 0) return rows;
    return rows.filter((h) => {
      if (h.label && h.label.toLowerCase().includes(q)) return true;
      // unitLabel only reads from `units` + `buildings`, both in deps below.
      if (unitLabel(h.unit_id).toLowerCase().includes(q)) return true;
      return false;
    });
  }, [rows, search, units, buildings]);

  // Scope narrows to one unit; the search box still filters within it.
  const scopedRows = unitId
    ? (filteredRows ?? []).filter((h) => h.unit_id === unitId)
    : filteredRows;

  return (
    <div className="space-y-6">
      {/* flex-wrap: at 360px the icon, title and "Add household" cannot share
          one row, and the button is the thing that has to move. min-w-0 lets
          the title column shrink rather than push. */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="groups" className="text-2xl" />
          </div>
          <div className="min-w-0">
            <h1 className="font-headline-md text-headline-md text-on-surface">
              Households
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Occupancy records — one row per household over a unit.
            </p>
          </div>
        </div>
        <Button
          onClick={() => setModal({ kind: 'create', row: null })}
          disabled={units.length === 0}
        >
          <Icon name="add" />
          <span>Add household</span>
        </Button>
      </header>

      <LocalOnlyBanner
        title="Local records only"
        body="Household records track occupancy locally — credential issuance and controller scope sync land later."
      />

      {toast ? <Toast message={toast} onDismiss={() => setToast(null)} /> : null}

      {rows !== null && (rows.length > 0 || search.length > 0) ? (
        <div className="flex items-center gap-2">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by label or unit"
            id="households-search"
          />
          {search.length > 0 && filteredRows ? (
            <span className="font-body-md text-body-md text-on-surface-variant">
              {filteredRows.length} of {rows.length}
            </span>
          ) : null}
        </div>
      ) : null}

      <PropertyScopeBar
        crumbs={
          unitId
            ? [
                { label: 'Buildings', href: '/buildings' },
                ...(buildingId
                  ? [{ label: 'Units', href: scopedHref('/units', { building_id: buildingId }) }]
                  : [{ label: 'Units', href: '/units' }]),
                { label: unitLabel(unitId) },
              ]
            : []
        }
        clearHref="/households"
        countLabel={
          unitId && rows ? `${scopedRows?.length ?? 0} of ${rows.length} households` : undefined
        }
      />

      <CrudTable<HouseholdRow>
        rows={scopedRows}
        error={error}
        loadingLabel="Loading households…"
        emptyMessage={
          rows && rows.length > 0 && search.length > 0
            ? `No households match "${search}". Try a different search.`
            : units.length === 0
              ? 'Add a unit first, then come back here to add a household.'
              : 'No households yet. Click Add household to create one.'
        }
        columns={[
          {
            key: 'label',
            header: 'Label',
            width: '22%',
            // Onward to the people in this household.
            render: (h) => (
              <Link
                to={scopedHref('/residents', {
                  building_id: buildingId,
                  unit_id: h.unit_id,
                  household_id: h.id,
                })}
                className="rounded-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {h.label ?? 'Household'}
              </Link>
            ),
          },
          {
            key: 'unit_id',
            header: 'Unit',
            render: (h) => unitLabel(h.unit_id),
          },
          {
            key: 'moved_in_at',
            header: 'Moved in',
            width: '14%',
            render: (h) => new Date(h.moved_in_at).toLocaleDateString(),
          },
          {
            key: 'moved_out_at',
            header: 'Status',
            width: '16%',
            render: (h) =>
              h.moved_out_at ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase bg-surface-container-high text-on-surface-variant">
                  Moved out
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase bg-secondary-container text-on-secondary-container">
                  Current
                </span>
              ),
          },
        ]}
        actions={(h) => (
          <RowActions
            actions={[
              { label: 'Edit', onClick: () => setModal({ kind: 'edit', row: h }) },
              {
                label: 'Move out',
                destructive: true,
                hidden: !!h.moved_out_at,
                onClick: () => setModal({ kind: 'move-out', row: h }),
              },
              {
                label: 'Delete',
                destructive: true,
                onClick: () => setModal({ kind: 'delete', row: h }),
              },
            ]}
          />
        )}
      />

      {modal ? (
        <HouseholdModal
          state={modal}
          units={units}
          buildings={buildings}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function HouseholdModal({
  state,
  units,
  buildings,
  onCancel,
  onDone,
}: {
  state: ModalState;
  units: UnitOption[];
  buildings: BuildingOption[];
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { kind, row } = state;
  const { key: idemKey } = useIdempotencyKey();
  const [unitId, setUnitId] = useState(row?.unit_id ?? units[0]?.id ?? '');
  const [label, setLabel] = useState(row?.label ?? '');
  const [movedIn, setMovedIn] = useState(
    row?.moved_in_at ? row.moved_in_at.slice(0, 10) : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (kind === 'create') {
        const body = CreateHouseholdBody.parse({
          unit_id: unitId,
          label: label.trim() || null,
          ...(movedIn ? { moved_in_at: new Date(movedIn).toISOString() } : {}),
        });
        await apiClient
          .withIdempotencyKey(idemKey)
          .post('/api/v1/households', body);
        onDone(`Household created.`);
        return;
      }
      if (kind === 'edit') {
        const body = UpdateHouseholdBody.parse({
          label: label.trim() || null,
          ...(movedIn ? { moved_in_at: new Date(movedIn).toISOString() } : {}),
        });
        await apiClient
          .withIdempotencyKey(idemKey)
          .patch(`/api/v1/households/${row!.id}`, body);
        onDone(`Household updated.`);
        return;
      }
      if (kind === 'move-out') {
        // Tranche CA: atomic move-out. The endpoint marks
        // household.moved_out_at, off-boards every linked resident
        // (suspend user, delete sessions, delete profile), and
        // cascade-deletes guest passes — all in one transaction.
        const res = await apiClient
          .withIdempotencyKey(idemKey)
          .post<{
            data: {
              household: HouseholdRow;
              removed_profiles: number;
              removed_guest_passes: number;
            };
          }>(`/api/v1/households/${row!.id}/move-out`, {});
        const { removed_profiles: removedProfiles, removed_guest_passes: removedPasses } =
          res.data;
        const parts = ['Household moved out.'];
        if (removedProfiles > 0) {
          parts.push(
            `${removedProfiles} resident${removedProfiles === 1 ? '' : 's'} off-boarded.`,
          );
        }
        if (removedPasses > 0) {
          parts.push(
            `${removedPasses} guest pass${removedPasses === 1 ? '' : 'es'} revoked.`,
          );
        }
        onDone(parts.join(' '));
        return;
      }
      if (kind === 'delete') {
        await apiClient.delete(`/api/v1/households/${row!.id}`);
        onDone(`Household deleted.`);
        return;
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed');
      setSubmitting(false);
    }
  }

  const titleByKind: Record<ModalKind, string> = {
    create: 'Add household',
    edit: 'Edit household',
    'move-out': 'Move out household',
    delete: 'Delete household',
  };
  const iconByKind: Record<ModalKind, string> = {
    create: 'add',
    edit: 'edit',
    'move-out': 'logout',
    delete: 'delete',
  };
  const tone: 'default' | 'destructive' =
    kind === 'delete' || kind === 'move-out' ? 'destructive' : 'default';
  const submitByKind: Record<ModalKind, string> = {
    create: 'Create',
    edit: 'Save',
    'move-out': 'Confirm move-out',
    delete: 'Delete',
  };

  const unitOpts = units.map((u) => {
    const b = buildings.find((x) => x.id === u.building_id);
    return { id: u.id, label: b ? `${b.name} · ${u.unit_number}` : u.unit_number };
  });

  return (
    <CrudModal
      /* eslint-disable security/detect-object-injection -- kind is a fixed union */
      title={titleByKind[kind]}
      icon={iconByKind[kind]}
      tone={tone}
      submitting={submitting}
      error={error}
      submitLabel={submitByKind[kind]}
      /* eslint-enable security/detect-object-injection */
      canSubmit={kind === 'delete' || kind === 'move-out' || unitId.length > 0}
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      {kind === 'delete' && (
        <p className="font-body-md text-body-md text-on-surface">
          Permanently delete this household record. Residents on it will lose
          their portal link. Prefer "Move out" for normal turnover.
        </p>
      )}
      {kind === 'move-out' && (
        <p className="font-body-md text-body-md text-on-surface">
          Marks this household vacated and off-boards every linked
          resident in one step: portal accounts are suspended, active
          sessions are killed, and any future guest passes are revoked.
          The audit trail keeps the full before/after — this is the
          right action for ordinary turnover.
        </p>
      )}
      {(kind === 'create' || kind === 'edit') && (
        <>
          {kind === 'create' && (
            <FormField id="household-unit" label="Unit">
              <select
                id="household-unit"
                required
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                className={fieldCls}
              >
                {unitOpts.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </select>
            </FormField>
          )}
          <FormField id="household-label" label="Label" hint="Optional.">
            <input
              id="household-label"
              maxLength={120}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className={fieldCls}
              placeholder="e.g. The Smiths"
            />
          </FormField>
          <FormField id="household-moved-in" label="Moved in" hint="Defaults to today.">
            <input
              id="household-moved-in"
              type="date"
              value={movedIn}
              onChange={(e) => setMovedIn(e.target.value)}
              className={fieldCls}
            />
          </FormField>
        </>
      )}
    </CrudModal>
  );
}
